import React, { useMemo, useRef, useState } from "react";
import { FiActivity, FiClock, FiMousePointer } from "react-icons/fi";
import useElementWidth from "../hooks/useElementWidth";
import { assessedSummary } from "./flowAssessment";
import { TONES, isPause, reading } from "./flowWords";
import "./ContinuityTimeline.css";

const MINUTE = 60 * 1000;
const DAY = 24 * 60 * MINUTE;
const MIN_PLOT_WIDTH = 640;
// Pixels a marker needs to itself: a dot with a two-digit count.
const MARKER_SPACE = 52;

export function timelineTimestamp(value) {
  if (value == null || value === "") return null;
  const date = new Date(value);
  const time = date.getTime();
  // Go's zero timestamp is a missing observation, not an event in year one.
  return Number.isFinite(time) && date.getFullYear() >= 2000 ? time : null;
}

function fullTime(value) {
  return new Date(value).toLocaleString(undefined, {
    month: "short", day: "numeric", hour: "2-digit", minute: "2-digit", second: "2-digit",
  });
}

const sameDay = (first, last) => new Date(first).toDateString() === new Date(last).toDateString();

// "09:01 AM to 09:04 AM", or one time when both ends share a minute.
function span(first, last) {
  const format = sameDay(first, last) ? { hour: "2-digit", minute: "2-digit" } : { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" };
  const [from, to] = [first, last].map((value) => new Date(value).toLocaleString(undefined, format));
  return from === to ? `at ${from}` : `${from} to ${to}`;
}

function deviceName(episode) {
  return episode.hostname || episode.device || episode.key?.device || "Unknown device";
}

// A marker says the same thing as the card it opens.
function assessment(episode) {
  const { tone, sure, label } = reading(episode);
  return { tone, label: tone === "located" || tone === "suspected" ? `${sure}: ${label}` : sure };
}

const rank = (event) => TONES.findIndex(({ tone }) => tone === event.state.tone);

// "Couldn't tell (1) · Brief pause, recovered on its own (7)".
const tally = (events) => TONES.map(({ tone, legend }) => [legend, events.filter((event) => event.state.tone === tone).length])
  .filter(([, count]) => count).map(([legend, count]) => `${legend} (${count})`).join(" · ");

const counted = (count, word) => `${count} ${word}${count === 1 ? "" : "s"}`;

function accessibleName({ episode, time, state }, classLabel) {
  return `View interruption: ${deviceName(episode)} · ${classLabel(episode.class) || "Unclassified"} · ${time === null ? "Time unavailable" : fullTime(time)} · ${state.label}`;
}

function makeDomain(events, startAt, endAt) {
  const first = events[0]?.time;
  const last = events[events.length - 1]?.time;
  const suppliedStart = timelineTimestamp(startAt);
  const suppliedEnd = timelineTimestamp(endAt);
  const dataStart = first ?? suppliedStart ?? suppliedEnd ?? Date.now();
  const dataEnd = last ?? suppliedEnd ?? suppliedStart ?? dataStart;
  const padding = Math.max((dataEnd - dataStart) * 0.05, 2 * MINUTE);
  let start = suppliedStart ?? dataStart - padding;
  let end = suppliedEnd ?? dataEnd + padding;
  // Range filtering belongs to the parent. Never silently hide a supplied record.
  start = Math.min(start, dataStart);
  end = Math.max(end, dataEnd);
  if (end <= start) {
    const center = (dataStart + dataEnd) / 2;
    start = center - 5 * MINUTE;
    end = center + 5 * MINUTE;
  }
  if (end - start < 10 * MINUTE) {
    const center = (start + end) / 2;
    start = center - 5 * MINUTE;
    end = center + 5 * MINUTE;
  }
  return { start, end };
}

function ticksFor(start, end) {
  const target = (end - start) / 5;
  const steps = [MINUTE, 5 * MINUTE, 15 * MINUTE, 30 * MINUTE, 60 * MINUTE,
    3 * 60 * MINUTE, 6 * 60 * MINUTE, 12 * 60 * MINUTE, DAY, 3 * DAY, 7 * DAY, 30 * DAY];
  const step = steps.find((value) => value >= target) || Math.ceil(target / DAY) * DAY;
  const ticks = [];
  for (let value = Math.ceil(start / step) * step; value <= end; value += step) {
    ticks.push({ value, position: ((value - start) / (end - start)) * 100 });
  }
  return ticks;
}

// A marker stands for one record, or for several on one device that would
// overlap. It shows, and opens, the most telling record it holds.
function markerOf(events) {
  return {
    key: events[0].episode.id, events, time: events[0].time, last: events[events.length - 1].time,
    lead: events.reduce((best, event) => (rank(event) < rank(best) ? event : best)),
  };
}

// One row per device, most interruptions first, including a device whose only
// records are hidden pauses. A record joins the marker before it when it sits within a marker's
// width of that marker's last record and within two of its first. Drawn at its
// midpoint, a marker is then never more than a marker's width from a record it
// holds, and never overlaps the next one.
function buildLanes(events, pauses, domain, width) {
  const space = (MARKER_SPACE / width) * 100;
  const lanes = new Map();
  const laneOf = ({ episode }) => {
    const key = episode.device || episode.key?.device || episode.hostname || "unknown";
    if (!lanes.has(key)) lanes.set(key, { key, name: deviceName(episode), address: episode.device || episode.key?.device, runs: [], kept: [] });
    return lanes.get(key);
  };
  pauses.forEach((event) => laneOf(event).kept.push(event));
  events.forEach((event) => {
    const { runs } = laneOf(event);
    // Insets leave every marker's full click target inside the plotting area.
    const placed = { ...event, position: 4 + ((event.time - domain.start) / (domain.end - domain.start)) * 92 };
    const run = runs[runs.length - 1];
    if (run && placed.position - run[run.length - 1].position < space && placed.position - run[0].position <= 2 * space) run.push(placed);
    else runs.push([placed]);
  });
  return [...lanes.values()]
    .map(({ runs, kept, ...lane }) => {
      const drawn = runs.flat();
      const pauses = [...drawn, ...kept].filter((event) => isPause(event.episode)).length;
      return {
        ...lane, pauses, interruptions: drawn.length + kept.length - pauses,
        markers: runs.map((run) => ({ ...markerOf(run), position: (run[0].position + run[run.length - 1].position) / 2 })),
      };
    })
    .sort((a, b) => b.interruptions - a.interruptions || b.pauses - a.pauses);
}

/** Recorded interruption starts, one row per device. Hidden brief pauses are counted
 * on their device's row but not drawn. The parent owns filtering and expanded evidence. */
export default function ContinuityTimeline({
  episodes = [], hiddenPauses = [], selectedId, onSelect, onClearSelection, startAt, endAt, detailId = "fc-selected-episode",
  classLabel = (key) => key,
}) {
  const buttons = useRef(new Map());
  const [plotRef, plotWidth] = useElementWidth(MIN_PLOT_WIDTH);
  const [hoveredKey, setHoveredKey] = useState(null);
  const [focusedKey, setFocusedKey] = useState(null);
  const model = useMemo(() => {
    const read = (row) => {
      const episode = assessedSummary(row);
      return { episode, time: timelineTimestamp(episode.stalled_at), state: assessment(episode) };
    };
    const all = episodes.map(read);
    const kept = hiddenPauses.map(read);
    const byId = (a, b) => String(a.episode.id).localeCompare(String(b.episode.id));
    const byTime = (a, b) => a.time - b.time || byId(a, b);
    const dated = all.filter((event) => event.time !== null).sort(byTime);
    const undated = all.filter((event) => event.time === null).sort(byId);
    // Hidden pauses keep their place in the time span, so showing them never rescales the map.
    const timed = [...dated, ...kept.filter((event) => event.time !== null)].sort(byTime);
    const domain = makeDomain(timed, startAt, endAt);
    const lanes = buildLanes(dated, kept, domain, plotWidth);
    const markers = [
      ...lanes.flatMap((lane) => lane.markers).sort((a, b) => a.time - b.time || String(a.key).localeCompare(String(b.key))),
      ...undated.map((event) => markerOf([event])),
    ];
    return { all, dated, undated, timed, domain, lanes, markers, ticks: ticksFor(domain.start, domain.end) };
  }, [episodes, hiddenPauses, startAt, endAt, plotWidth]);
  const holding = (id) => model.markers.find((marker) => marker.events.some((event) => event.episode.id === id));
  const selected = selectedId ? holding(selectedId) : undefined;
  const selectedEvent = selected?.events.find((event) => event.episode.id === selectedId);
  const preview = model.markers.find((marker) => marker.key === (hoveredKey || focusedKey));
  const displayed = preview || selected;
  const isPreview = Boolean(displayed) && displayed !== selected;
  // A previewed group is described as a whole; otherwise one record is.
  const described = !displayed ? null : !isPreview ? selectedEvent : displayed.events.length === 1 ? displayed.lead : null;
  const tones = new Set(model.all.map((event) => event.state.tone));
  const grouped = model.markers.some((marker) => marker.events.length > 1);
  const longRange = model.domain.end - model.domain.start > DAY;

  const focusMarker = (key) => {
    const button = buttons.current.get(key);
    button?.focus({ preventScroll: true });
    button?.scrollIntoView?.({ block: "nearest", inline: "nearest" });
  };

  const clearOnEscape = (event) => {
    if (event.key !== "Escape" || !onClearSelection || !selectedId) return false;
    event.preventDefault();
    onClearSelection();
    return true;
  };

  const navigate = (event, key) => {
    if (clearOnEscape(event)) return;
    const index = model.markers.findIndex((marker) => marker.key === key);
    let next;
    if (event.key === "ArrowRight") next = Math.min(model.markers.length - 1, index + 1);
    if (event.key === "ArrowLeft") next = Math.max(0, index - 1);
    if (event.key === "Home") next = 0;
    if (event.key === "End") next = model.markers.length - 1;
    if (next === undefined) return;
    event.preventDefault();
    focusMarker(model.markers[next]?.key);
  };

  const markerButton = (marker, undated = false) => {
    const isSelected = marker === selected;
    // The record a click acts on: the one open here, or the one this marker shows.
    const target = isSelected ? selectedEvent : marker.lead;
    const count = marker.events.length;
    const name = count > 1
      ? `View ${count} interruptions: ${deviceName(marker.lead.episode)} · ${span(marker.time, marker.last)} · ${tally(marker.events)}`
      : accessibleName(marker.lead, classLabel);
    return (
      <button
        type="button"
        ref={(element) => { if (element) buttons.current.set(marker.key, element); else buttons.current.delete(marker.key); }}
        className={`ct-event ct-tone-${marker.lead.state.tone}${count > 1 ? " ct-group" : ""}${isSelected ? " is-selected" : ""}${undated ? " ct-event-undated" : ""}`}
        aria-label={name}
        aria-pressed={isSelected}
        aria-expanded={isSelected}
        aria-controls={detailId}
        data-event-id={marker.key}
        data-episode-id={target.episode.id}
        data-time-position={marker.position}
        data-count={count}
        onMouseEnter={() => setHoveredKey(marker.key)}
        onMouseLeave={() => setHoveredKey((key) => key === marker.key ? null : key)}
        onFocus={() => { setHoveredKey(null); setFocusedKey(marker.key); }}
        onBlur={() => setFocusedKey((key) => key === marker.key ? null : key)}
        onClick={() => onSelect?.(target.episode.id)}
        onKeyDown={(keyboardEvent) => navigate(keyboardEvent, marker.key)}
      >
        <span className="ct-dot" aria-hidden="true"><span className="ct-marker"><span /></span>{count > 1 && <span className="ct-count">{count}</span>}</span>
        {undated && <span className="ct-undated-label">{deviceName(marker.lead.episode)}<small>{classLabel(marker.lead.episode.class) || "Unclassified"}</small></span>}
      </button>
    );
  };

  if (!episodes.length && !hiddenPauses.length) {
    return <div className="ct-empty"><FiActivity aria-hidden="true" /><strong>No recorded interruptions in this view.</strong><p>Try another time range or adjust the filters.</p></div>;
  }

  return (
    <section className="ct-timeline" aria-label="Recorded interruption timeline">
      <div className="ct-heading">
        <div><span className="ct-eyebrow">One row per device</span><strong>{model.timed.length ? `${new Date(model.timed[0].time).toLocaleDateString(undefined, { month: "short", day: "numeric" })}${new Date(model.timed[0].time).toDateString() !== new Date(model.timed[model.timed.length - 1].time).toDateString() ? ` – ${new Date(model.timed[model.timed.length - 1].time).toLocaleDateString(undefined, { month: "short", day: "numeric" })}` : ""}` : "Records without a start time"}</strong></div>
        <span className="ct-timezone"><FiClock aria-hidden="true" />Local time</span>
      </div>
      {model.dated.length > 0 && <div className="ct-mobile-navigation"><span>Swipe to explore time</span><button type="button" onClick={() => focusMarker(holding(model.dated[model.dated.length - 1].episode.id).key)}>Latest event <span aria-hidden="true">→</span></button></div>}
      {model.lanes.length > 0 && <div className="ct-scroll" tabIndex={0} role="group" aria-label="Timeline map. Scroll to view more devices or times.">
        <div className="ct-canvas">
          <div className="ct-axis">
            <span className="ct-device-heading">Device <span>{model.lanes.length}</span></span>
            <div ref={plotRef} className="ct-time-axis" aria-hidden="true">{model.ticks.map((tick) => <span key={tick.value} style={{ left: `${4 + tick.position * 0.92}%` }}><span>{new Date(tick.value).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" })}</span>{longRange && <small>{new Date(tick.value).toLocaleDateString(undefined, { month: "short", day: "numeric" })}</small>}</span>)}</div>
          </div>
          {model.lanes.map((lane) => <div className="ct-lane" key={lane.key}>
            <div className="ct-device"><div className="ct-device-info"><strong title={lane.name}>{lane.name}</strong>{[lane.interruptions && counted(lane.interruptions, "interruption"), lane.pauses && counted(lane.pauses, "brief pause")].filter(Boolean).map((line, index) => <span key={line}>{index === 0 && lane.address && lane.address !== lane.name ? `${lane.address} · ` : ""}{line}</span>)}</div></div>
            <ul className="ct-track" aria-label={`${lane.name} interruptions`}>
              <li className="ct-grid" aria-hidden="true">{model.ticks.map((tick) => <i key={tick.value} style={{ left: `${4 + tick.position * 0.92}%` }} />)}<b /></li>
              {lane.markers.map((marker) => <li className="ct-event-position" key={marker.key} style={{ left: `${marker.position}%` }}>{markerButton(marker)}</li>)}
            </ul>
          </div>)}
        </div>
      </div>}
      {model.undated.length > 0 && <div className="ct-undated"><span>Time unavailable <small>{model.undated.length}</small></span><ul>{model.markers.filter((marker) => marker.time === null).map((marker) => <li key={marker.key}>{markerButton(marker, true)}</li>)}</ul></div>}
      <div className="ct-footer">
        <div className="ct-legend" aria-label="Map legend">{TONES.filter(({ tone }) => tones.has(tone)).map(({ tone, legend }) => <span key={tone} className={`ct-tone-${tone}`}><span className="ct-marker" aria-hidden="true"><span /></span>{legend}</span>)}{grouped && <span><i className="ct-legend-count" aria-hidden="true">3</i>Several close together</span>}</div>
        <p>Each dot is the moment a connection froze, not how long it lasted.{grouped && " A dot with a number holds several that froze close together, and shows the one to look at first."} A quiet stretch only means nothing being watched froze.{hiddenPauses.length === 1 && " 1 brief pause is hidden: a connection that stopped for a few seconds and recovered on its own."}{hiddenPauses.length > 1 && ` ${hiddenPauses.length} brief pauses are hidden: connections that stopped for a few seconds and recovered on their own.`}</p>
      </div>
      {displayed ? <div className={`ct-selection ct-tone-${(described || displayed.lead).state.tone}${isPreview ? " is-preview" : ""}`} role="status"><span className="ct-selection-mode">{isPreview ? "Preview" : "Selected"}</span><span className="ct-selection-dot" aria-hidden="true" />
        {described ? <div><strong>{classLabel(described.episode.class) || "Unclassified"}<span> · {deviceName(described.episode)}</span></strong><span>{described.time === null ? "Time unavailable" : fullTime(described.time)} · {described.state.label}</span></div>
          : <div><strong>{displayed.events.length} interruptions<span> · {deviceName(displayed.lead.episode)}</span></strong><span>{span(displayed.time, displayed.last)} · {tally(displayed.events)}</span></div>}
        <span className="ct-selection-hint">{isPreview ? "Click to see what happened" : "Explained below"}</span></div>
        : <div className="ct-guide"><FiMousePointer aria-hidden="true" /><span>Click a dot to see what happened and where it broke.</span><small>Arrow keys move between dots</small></div>}
      {selected?.events.length > 1 && <div className="ct-members" role="group" aria-label={`This dot holds ${selected.events.length} interruptions`}>
        <span>This dot holds {selected.events.length}, {span(selected.time, selected.last)}</span>
        <ul>{selected.events.map((event) => <li key={event.episode.id}>
          <button type="button" className={`ct-member ct-tone-${event.state.tone}${event === selectedEvent ? " is-selected" : ""}`} aria-label={accessibleName(event, classLabel)} aria-pressed={event === selectedEvent} aria-controls={detailId} onClick={() => onSelect?.(event.episode.id)} onKeyDown={clearOnEscape}>
            <span className="ct-marker" aria-hidden="true"><span /></span><time dateTime={event.episode.stalled_at}>{sameDay(selected.time, selected.last) ? new Date(event.time).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit", second: "2-digit" }) : fullTime(event.time)}</time><span>{classLabel(event.episode.class) || "Unclassified"}</span>
          </button>
        </li>)}</ul>
      </div>}
    </section>
  );
}
